/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Property } from 'csstype';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist } from 'src/core/themes/luisStylist';
import { numberToPercentageString } from 'src/core/utils/number';
import { BarTooltipHeader } from 'src/luis/projects/analytics/components/PredictionsPerIntentCard/BarTooltip/BarTooltipHeader';
import { getDistributionChartConfig } from 'src/luis/projects/analytics/components/PredictionsPerIntentCard/distributionChartHelpers';
import { LegendItem } from 'src/luis/projects/analytics/components/TrainingEvaluation/Legend/LegendItem';
import { Row } from 'src/luis/projects/analytics/components/TrainingEvaluation/Legend/LegendRowHelpers';
import { IntentMetadata } from 'src/luis/projects/analytics/types/IntentMetadata';
import { LuisId } from 'src/luis/types/Resource';

export type BarTooltipProps = {
    /**
     * The index of the bar.
     */
    index: number;

    /**
     * Flag indicates if the distribution chart
     * in number mode or percentage.
     */
    isNumber: boolean;

    /**
     * The sort type currently applied.
     */
    sortType: string;

    /**
     * A map where the key is the intent id and the
     * value is the metadata of this intent.
     */
    utterancesPerIntent: Map<LuisId, IntentMetadata>;
};

const messages = defineMessages({
    tooltipTotalLegend: {
        id: 'analytics.predictionsPerIntentCard.tooltipTotalLegend',
        defaultMessage: 'Total number of utterances:'
    },
    tooltipIncorrectLegend: {
        id: 'analytics.predictionsPerIntentCard.tooltipIncorrectLegend',
        defaultMessage: 'Incorrectly predicted:'
    },
    tooltipCorrectLegend: {
        id: 'analytics.predictionsPerIntentCard.tooltipCorrectLegend',
        defaultMessage: 'Correctly predicted:'
    },
    tooltipUnclearLegend: {
        id: 'analytics.predictionsPerIntentCard.tooltipUnclearLegend',
        defaultMessage: 'Unclear:'
    }
});

const { styleDiv } = getStylist('BarTooltip');

const TooltipContainer = styleDiv('TooltipContainer', theme => ({
    boxShadow: theme.boxShadow,
    backgroundColor: theme.backgroundColor,
    fontWeight: theme.fontWeight as Property.FontWeight
}));

const TooltipBodyContainer = styleDiv('TooltipBodyContainer', theme => ({
    padding: theme.bodySpacing
}));

const Label = styleDiv('Label', theme => ({
    display: 'table-cell',
    width: theme.labelWidth
}));

const Value = styleDiv('Value', theme => ({
    textAlign: 'right',
    display: 'table-cell',
    paddingRight: theme.valueSpacing,
    fontWeight: theme.dataFontWeight as Property.FontWeight
}));

/**
 * The Tooltip to be displayed on hovering on the bar.
 *
 * @param props the properties of the tooltip.
 */
export const BarTooltip = (props: BarTooltipProps) => {
    const { index, isNumber, sortType, utterancesPerIntent } = props;
    const { localize } = useLocalization();

    const { xData, yData } = getDistributionChartConfig(isNumber, sortType, { lastTrainingDate: new Date(), utterancesPerIntent });

    const title = xData.data[index];
    const correct = yData.tooltipData[0][index];
    const unclear = yData.tooltipData[1][index];
    const incorrect = yData.tooltipData[2][index];
    const total = correct + incorrect + unclear;

    return (
        <TooltipContainer>
            <BarTooltipHeader title={title} />

            <TooltipBodyContainer>
                <Row>
                    <Label>{localize(messages.tooltipTotalLegend)}</Label>
                    <Value>{total}</Value>
                </Row>

                <Row>
                    <Label>
                        <LegendItem color="incorrect" />
                        {localize(messages.tooltipIncorrectLegend)}
                    </Label>
                    <Value>{incorrect}</Value>
                    <span>({numberToPercentageString({ ratio: incorrect / total, decimals: 1 })})</span>
                </Row>

                <Row>
                    <Label>
                        <LegendItem color="correct" />
                        {localize(messages.tooltipCorrectLegend)}
                    </Label>
                    <Value>{correct}</Value>
                    <span>({numberToPercentageString({ ratio: correct / total, decimals: 1 })})</span>
                </Row>

                <Row>
                    <Label>
                        <LegendItem color="unclear" />
                        {localize(messages.tooltipUnclearLegend)}
                    </Label>
                    <Value>{unclear}</Value>
                    <span>({numberToPercentageString({ ratio: unclear / total, decimals: 1 })})</span>
                </Row>
            </TooltipBodyContainer>
        </TooltipContainer>
    );
};
